import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import faqData from 'data/faqData.json';
import TitleHelmet from 'components/TitleHelmet';
import ReactMarkdown, { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import ProductCard from '../components/ProductCard';
import OtherCalcs from 'components/OtherCalcs';
import { getFaqIcon, getCleanDescription } from '../utils/faqUtils';

interface FAQItem {
  id: string;
  question: string;
  answer?: string;
  filePath?: string;
  linkURL?: string;
  hidden?: boolean;
}

interface RouteParams {
  [key: string]: string | undefined;
  faqId: string;
}

const FAQPage: React.FC = () => {
  const { faqId } = useParams<RouteParams>();
  const navigate = useNavigate();
  const faqItem = (faqData as FAQItem[]).find(item => item.id === faqId);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearch = () => {
    const searchValue = searchInputRef.current?.value.trim();
    if (searchValue) {
      window.location.href = `/faq?search=${encodeURIComponent(searchValue)}`;
    }
  };

  const visibleFaqs = (faqData as FAQItem[]).filter(item => !item.hidden);
  const currentIndex = visibleFaqs.findIndex(item => item.id === faqId);
  const prevFaq = currentIndex > 0 ? visibleFaqs[currentIndex - 1] : null;
  const nextFaq = currentIndex < visibleFaqs.length - 1 ? visibleFaqs[currentIndex + 1] : null;

  const customMarkdownComponents: Components = {
    img: ({ node, ...props }) => {
      const altText = props.alt || '';
      const widthMatch = altText.match(/-(\d+)px$/);
      const width = widthMatch ? `${widthMatch[1]}px` : props.width || '100%';
      const cleanAlt = widthMatch ? altText.replace(/-\d+px$/, '') : altText;

      return (
        <img
          {...props}
          style={{
            maxWidth: width,
            height: 'auto',
            margin: '1em 0',
            borderRadius: '8px',
            display: 'block',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          alt={cleanAlt}
        />
      );
    },
    a: ({ node, href, children, ...props }) => {
      // Check if this is a product card
      if (href && props.title && props.title.startsWith('product-card|')) {
        const titleParts = props.title.split('|');
        if (titleParts.length >= 2) {
          const imageUrl = titleParts[1];
          const description = titleParts[2] || undefined;
          const productTitle = Array.isArray(children) ? children.join('') : String(children || '');

          return <ProductCard title={productTitle} imageUrl={imageUrl} affiliateUrl={href} description={description} />;
        }
      }

      // Check if this is a YouTube link that should be embedded
      if (href && (href.includes('youtube.com/watch?v=') || href.includes('youtu.be/'))) {
        const videoId = href.includes('youtube.com/watch?v=')
          ? href.split('v=')[1]?.split('&')[0]
          : href.split('youtu.be/')[1]?.split('?')[0];

        if (videoId && props.title === 'embed') {
          return (
            <div
              style={{
                position: 'relative',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                overflow: 'hidden',
                margin: '1em 0',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Embedded YouTube Video"
              />
            </div>
          );
        }
      }

      // Regular link rendering
      return (
        <a href={href} {...props}>
          {children}
        </a>
      );
    },
    h1: ({ children }) => <h1 className="faq-content-h1">{children}</h1>,
    h2: ({ children }) => <h2 className="faq-content-h2">{children}</h2>,
    h3: ({ children }) => <h3 className="faq-content-h3">{children}</h3>,
    p: ({ children }) => <p className="faq-content-p">{children}</p>,
    ul: ({ children }) => <ul className="faq-content-ul">{children}</ul>,
    ol: ({ children }) => <ol className="faq-content-ol">{children}</ol>,
    li: ({ children }) => <li className="faq-content-li">{children}</li>,
    blockquote: ({ children }) => <blockquote className="faq-content-blockquote">{children}</blockquote>,
  };

  useEffect(() => {
    if (!faqItem || faqItem.hidden) {
      navigate('/404', { replace: true });
      return;
    }

    if (faqItem.linkURL) {
      navigate(faqItem.linkURL, { replace: true });
      return;
    }

    setIsLoading(true);

    if (faqItem.filePath) {
      fetch(process.env.PUBLIC_URL + faqItem.filePath)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(text => {
          setMarkdownContent(text);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Failed to fetch FAQ content:', error);
          setMarkdownContent('Failed to load content.');
          setIsLoading(false);
        });
    } else if (faqItem.answer) {
      setMarkdownContent(faqItem.answer);
      setIsLoading(false);
    }
  }, [faqItem, navigate, faqId]);

  if (!faqItem || faqItem.hidden) {
    return null;
  }

  return (
    <div className="faq-page">
      <TitleHelmet
        title={`${faqItem.question} | FAQ`}
        description={getCleanDescription(markdownContent, faqItem.question)}
      />

      {/* Compact Search Bar */}
      <div className="faq-compact-search">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search other questions..."
            className="faq-search-input"
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          <button className="faq-search-button" onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <nav className="faq-breadcrumb">
        <a href="/faq" className="breadcrumb-link">
          <span className="breadcrumb-icon">💬</span>
          All FAQs
        </a>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-current">{faqItem.question}</span>
      </nav>

      {/* Main Content - Full Width */}
      <div className="faq-content-container">
        <div className="faq-question-header">
          <h1 className="faq-question-title">{faqItem.question}</h1>
        </div>

        <div className="faq-content">
          {isLoading ? (
            <div className="faq-loading">
              <span className="loading-spinner">⏳</span>
              <p>Loading answer...</p>
            </div>
          ) : (
            <div className="faq-answer">
              <ReactMarkdown components={customMarkdownComponents} rehypePlugins={[rehypeRaw]}>
                {markdownContent}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      {/* Navigation between FAQs */}
      <div className="faq-navigation">
        <div className="faq-nav-buttons">
          {prevFaq && (
            <a href={prevFaq.linkURL ? prevFaq.linkURL : `/faq/${prevFaq.id}`} className="faq-nav-btn prev">
              <span className="nav-arrow">← Previous</span>
              <span className="nav-question">{prevFaq.question}</span>
            </a>
          )}
          {nextFaq && (
            <a href={nextFaq.linkURL ? nextFaq.linkURL : `/faq/${nextFaq.id}`} className="faq-nav-btn next">
              <span className="nav-arrow">Next →</span>
              <span className="nav-question">{nextFaq.question}</span>
            </a>
          )}
        </div>
      </div>

      {/* Other Questions Section */}
      <div className="faq-other-questions">
        <h3 className="other-questions-title">
          <span className="other-questions-icon">🔗</span>
          Other Questions
        </h3>
        <div className="other-questions-grid">
          {visibleFaqs
            .filter(item => item.id !== faqId)
            .slice(0, 6)
            .map(item => (
              <a key={item.id} href={item.linkURL ? item.linkURL : `/faq/${item.id}`} className="other-question-card">
                <span className="other-question-icon">{getFaqIcon(item.question)}</span>
                <span className="other-question-text">{item.question}</span>
              </a>
            ))}
        </div>
        {/* <div className="view-all-section">
          <a href="/faq" className="view-all-btn">
            View all questions →
          </a>
        </div> */}
      </div>
      <OtherCalcs sections="calcs" />
    </div>
  );
};

export default FAQPage;
